import React, { useState, useEffect, useCallback } from 'react';

/**
 * ConnectorAdmin — Smart Connector Management + Sentence Builder
 *
 * Manages connectors (because, so, but, etc.) and provides a live
 * sentence builder that shows how the pipeline splits and translates
 * compound sentences.
 *
 * DB: sentence_connectors in Grammar_DB_DEV
 */

const CATEGORIES = ['all', 'causal', 'contrast', 'temporal', 'conditional', 'additive', 'other'];

const ConnectorAdmin = ({ API, adminToken, isDarkMode }) => {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeSection, setActiveSection] = useState('list'); // list | builder
  const [editConnector, setEditConnector] = useState(null);
  const [newConnector, setNewConnector] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Sentence builder state
  const [testSentence, setTestSentence] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  // Example sentence state
  const [newExample, setNewExample] = useState({ connectorId: null, english: '', latin: '', arabic: '' });


  const dk = isDarkMode;

  const fetchConnectors = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/grammar/connectors?`;
      if (activeCategory !== 'all') url += `category=${activeCategory}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      const data = await res.json();
      if (data.success) setConnectors(data.connectors || []);
    } catch (err) {
      console.error('Failed to load connectors:', err);
    }
    setLoading(false);
  }, [API, adminToken, activeCategory, searchTerm]);

  useEffect(() => { fetchConnectors(); }, [fetchConnectors]);

  const handleSave = async (isNew) => {
    const data = isNew ? newConnector : editConnector;
    if (!data || !data.english) return;
    setSaving(true);
    try {
      const url = isNew ? `${API}/admin/grammar/connectors` : `${API}/admin/grammar/connectors/${data.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(data)
      });
      const result = await res.json();
      if (result.success) {
        if (isNew) setNewConnector(null);
        else setEditConnector(null);
        fetchConnectors();
      }
    } catch (err) {
      console.error('Failed to save:', err);
    }
    setSaving(false);
  };

  const handleDelete = async (id, english) => {
    setConfirmDialog({
      title: 'Delete Connector',
      message: `Delete "${english}"? This cannot be undone.`,
      confirmColor: 'red',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await fetch(`${API}/admin/grammar/connectors/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          fetchConnectors();
        } catch (err) { console.error(err); }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleTest = async () => {
    if (!testSentence.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/admin/grammar/connectors/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ sentence: testSentence })
      });
      const data = await res.json();
      if (data.success) setTestResult(data);
    } catch (err) {
      console.error('Test failed:', err);
    }
    setTesting(false);
  };

  const handleAddExample = async () => {
    if (!newExample.connectorId || !newExample.english) return;
    try {
      const res = await fetch(`${API}/admin/grammar/connectors/${newExample.connectorId}/examples`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ english: newExample.english, latin: newExample.latin, arabic: newExample.arabic })
      });
      const data = await res.json();
      if (data.success) {
        setNewExample({ connectorId: null, english: '', latin: '', arabic: '' });
        fetchConnectors();
      }
    } catch (err) { console.error(err); }
  };

  const handleRemoveExample = async (connectorId, index) => {
    try {
      await fetch(`${API}/admin/grammar/connectors/${connectorId}/examples/${index}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      fetchConnectors();
    } catch (err) { console.error(err); }
  };

  const inputCls = `w-full px-2 py-1 rounded border text-sm ${dk ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`;
  const inputClsAr = `w-full px-2 py-1 rounded border text-sm text-right ${dk ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`;
  const catColors = { causal: 'text-orange-500', contrast: 'text-purple-500', temporal: 'text-blue-500', conditional: 'text-yellow-500', additive: 'text-green-500', other: 'text-gray-500' };

  const renderConnectorForm = (data, setData, isNew) => (
    <div className={`p-4 rounded-lg border mb-4 ${dk ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
      <h4 className={`font-bold mb-3 ${dk ? 'text-white' : 'text-gray-800'}`}>{isNew ? 'Add New Connector' : `Edit: ${data.english}`}</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
        <div>
          <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>English</label>
          <input type="text" value={data.english || ''} onChange={e => setData({ ...data, english: e.target.value })} className={inputCls} placeholder="because" />
        </div>
        <div>
          <label className={`text-xs font-medium ${dk ? 'text-emerald-400' : 'text-emerald-600'}`}>Latin Script</label>
          <input type="text" value={data.latin || ''} onChange={e => setData({ ...data, latin: e.target.value })} className={inputCls} placeholder="hit" />
        </div>
        <div>
          <label className={`text-xs font-medium ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Arabic Script</label>
          <input type="text" dir="rtl" value={data.arabic || ''} onChange={e => setData({ ...data, arabic: e.target.value })} className={inputClsAr} placeholder="حيت" />
        </div>
        <div>
          <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Alt Latin</label>
          <input type="text" value={data.alt_latin || ''} onChange={e => setData({ ...data, alt_latin: e.target.value })} className={inputCls} placeholder="optional" />
        </div>
        <div>
          <label className={`text-xs font-medium ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Alt Arabic</label>
          <input type="text" dir="rtl" value={data.alt_arabic || ''} onChange={e => setData({ ...data, alt_arabic: e.target.value })} className={inputClsAr} placeholder="optional" />
        </div>
        <div>
          <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Category</label>
          <select value={data.category || 'other'} onChange={e => setData({ ...data, category: e.target.value })} className={inputCls}>
            {CATEGORIES.filter(c => c !== 'all').map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={() => isNew ? setNewConnector(null) : setEditConnector(null)} className={`px-3 py-1 rounded text-sm ${dk ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>Cancel</button>
        <button onClick={() => handleSave(isNew)} disabled={saving} className="px-3 py-1 rounded text-sm bg-emerald-600 text-white hover:bg-emerald-700">
          {saving ? 'Saving...' : isNew ? 'Add Connector' : 'Save Changes'}
        </button>
      </div>
    </div>
  );

  return (
    <div className={`p-6 rounded-lg border ${dk ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>

      {/* Confirm Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg max-w-sm ${dk ? 'bg-gray-800' : 'bg-white'}`}>
            <h4 className={`font-bold mb-2 ${dk ? 'text-white' : 'text-gray-800'}`}>{confirmDialog.title}</h4>
            <p className={`mb-4 text-sm ${dk ? 'text-gray-300' : 'text-gray-600'}`}>{confirmDialog.message}</p>
            <div className="flex gap-2 justify-end">
              <button onClick={confirmDialog.onCancel} className={`px-3 py-1 rounded ${dk ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>Cancel</button>
              <button onClick={confirmDialog.onConfirm} className="px-3 py-1 rounded text-white bg-red-600">{confirmDialog.confirmText}</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-lg font-bold ${dk ? 'text-white' : 'text-gray-800'}`}>Smart Connectors</h3>
        <span className={`text-xs ${dk ? 'text-gray-400' : 'text-gray-500'}`}>{connectors.length} connectors</span>
      </div>

      {/* Section Tabs: List | Sentence Builder */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveSection('list')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${activeSection === 'list' ? 'bg-emerald-600 text-white' : dk ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
        >
          Connector List
        </button>
        <button
          onClick={() => setActiveSection('builder')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${activeSection === 'builder' ? 'bg-emerald-600 text-white' : dk ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
        >
          Sentence Builder
        </button>
      </div>

      {/* ═══════════ SECTION A: CONNECTOR LIST ═══════════ */}
      {activeSection === 'list' && (
        <div>
          {/* Category Filter + Search + Add Button */}
          <div className="flex gap-2 mb-3 flex-wrap">
            {CATEGORIES.map(c => (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                className={`px-2.5 py-1 rounded text-xs font-medium ${
                  activeCategory === c ? 'bg-emerald-600 text-white' : dk ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex gap-2 mb-4">
            <input
              type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search connectors..." className={`flex-1 px-3 py-2 rounded-lg border text-sm ${dk ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300'}`}
            />
            <button onClick={() => setNewConnector({ english: '', latin: '', arabic: '', alt_latin: '', alt_arabic: '', category: 'other' })}
              className="px-4 py-2 rounded-lg text-sm bg-emerald-600 text-white hover:bg-emerald-700">+ Add</button>
          </div>

          {/* New Connector Form */}
          {newConnector && renderConnectorForm(newConnector, setNewConnector, true)}

          {/* Edit Connector Form */}
          {editConnector && renderConnectorForm(editConnector, setEditConnector, false)}

          {/* Loading */}
          {loading && <div className={`text-center py-8 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</div>}

          {/* Connector Cards */}
          {!loading && connectors.length === 0 && (
            <div className={`text-center py-8 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>No connectors found</div>
          )}

          {!loading && connectors.map(c => (
            <div key={c.id} className={`mb-2 p-3 rounded-lg border ${dk ? 'border-gray-700 hover:bg-gray-750' : 'border-gray-200 hover:bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <span className={`font-semibold text-sm ${dk ? 'text-white' : 'text-gray-800'}`}>{c.english}</span>
                  <span className={`font-mono text-sm ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>{c.latin}</span>
                  <span className={`font-mono text-sm ${dk ? 'text-amber-400' : 'text-amber-700'}`} dir="rtl">{c.arabic}</span>
                  {c.alt_latin && <span className={`text-xs ${dk ? 'text-gray-500' : 'text-gray-400'}`}>alt: {c.alt_latin}</span>}
                  <span className={`text-xs ${catColors[c.category] || 'text-gray-400'}`}>{c.category}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditConnector({ ...c })} className={`text-xs px-2 py-1 rounded ${dk ? 'text-blue-400 hover:bg-gray-600' : 'text-blue-600 hover:bg-blue-50'}`}>Edit</button>
                  <button onClick={() => handleDelete(c.id, c.english)} className={`text-xs px-2 py-1 rounded ${dk ? 'text-red-400 hover:bg-gray-600' : 'text-red-600 hover:bg-red-50'}`}>Delete</button>
                </div>
              </div>

              {/* Examples */}
              {c.examples && c.examples.length > 0 && (
                <div className={`mt-2 pl-4 border-l-2 ${dk ? 'border-gray-600' : 'border-gray-200'}`}>
                  <p className={`text-xs font-medium mb-1 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Examples:</p>
                  {c.examples.map((ex, i) => (
                    <div key={i} className="flex items-start gap-2 mb-1">
                      <div className="flex-1">
                        <span className={`text-xs ${dk ? 'text-gray-300' : 'text-gray-600'}`}>{ex.english}</span>
                        {ex.latin && <span className={`text-xs ml-2 ${dk ? 'text-emerald-400' : 'text-emerald-600'}`}>{ex.latin}</span>}
                        {ex.arabic && <span className={`text-xs ml-2 ${dk ? 'text-amber-400' : 'text-amber-600'}`} dir="rtl">{ex.arabic}</span>}
                      </div>
                      <button onClick={() => handleRemoveExample(c.id, i)} className={`text-xs ${dk ? 'text-red-400' : 'text-red-500'}`}>x</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Example */}
              {newExample.connectorId === c.id ? (
                <div className={`mt-2 p-2 rounded ${dk ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <input type="text" value={newExample.english} onChange={e => setNewExample({ ...newExample, english: e.target.value })} placeholder="English sentence" className={inputCls} />
                    <input type="text" value={newExample.latin} onChange={e => setNewExample({ ...newExample, latin: e.target.value })} placeholder="Latin" className={inputCls} />
                    <input type="text" dir="rtl" value={newExample.arabic} onChange={e => setNewExample({ ...newExample, arabic: e.target.value })} placeholder="Arabic" className={inputClsAr} />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setNewExample({ connectorId: null, english: '', latin: '', arabic: '' })} className={`text-xs px-2 py-1 rounded ${dk ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>Cancel</button>
                    <button onClick={handleAddExample} className="text-xs px-2 py-1 rounded bg-emerald-600 text-white">Save Example</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setNewExample({ connectorId: c.id, english: '', latin: '', arabic: '' })} className={`mt-1 text-xs ${dk ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  + Add example
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ═══════════ SECTION B: SENTENCE BUILDER ═══════════ */}
      {activeSection === 'builder' && (
        <div>
          <p className={`text-sm mb-3 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>
            Type an English sentence with a connector. The system will show how it splits, translates each part, and joins them.
          </p>

          {/* Input */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={testSentence}
              onChange={e => setTestSentence(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleTest()}
              placeholder="e.g. I went home because I was tired"
              className={`flex-1 px-3 py-2.5 rounded-lg border text-sm ${dk ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300'}`}
            />
            <button onClick={handleTest} disabled={testing}
              className="px-5 py-2.5 rounded-lg text-sm bg-emerald-600 text-white hover:bg-emerald-700 font-medium">
              {testing ? 'Testing...' : 'Test'}
            </button>
          </div>

          {/* Result */}
          {testResult && (
            <div className={`p-4 rounded-lg border ${dk ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              {testResult.has_connector ? (
                <div>
                  <h4 className={`font-bold mb-3 text-sm ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>Sentence Breakdown</h4>

                  {/* Clause 1 */}
                  <div className={`mb-2 p-3 rounded ${dk ? 'bg-gray-800' : 'bg-white'} border ${dk ? 'border-gray-600' : 'border-gray-200'}`}>
                    <p className={`text-xs font-medium mb-1 ${dk ? 'text-blue-400' : 'text-blue-600'}`}>Clause 1</p>
                    <div className="flex items-center gap-4">
                      <span className={`text-sm ${dk ? 'text-gray-300' : 'text-gray-600'}`}>{testResult.breakdown.clause1.english}</span>
                      <span className={`font-mono text-sm ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>{testResult.breakdown.clause1.latin}</span>
                      <span className={`font-mono text-sm ${dk ? 'text-amber-400' : 'text-amber-700'}`} dir="rtl">{testResult.breakdown.clause1.arabic}</span>
                    </div>
                  </div>

                  {/* Connector */}
                  <div className={`mb-2 p-3 rounded ${dk ? 'bg-emerald-900/30' : 'bg-emerald-50'} border ${dk ? 'border-emerald-800' : 'border-emerald-200'}`}>
                    <p className={`text-xs font-medium mb-1 ${dk ? 'text-emerald-400' : 'text-emerald-600'}`}>Connector</p>
                    <div className="flex items-center gap-4">
                      <span className={`text-sm font-bold ${dk ? 'text-white' : 'text-gray-800'}`}>{testResult.breakdown.connector.english}</span>
                      <span className={`font-mono text-sm font-bold ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>{testResult.breakdown.connector.latin}</span>
                      <span className={`font-mono text-sm font-bold ${dk ? 'text-amber-400' : 'text-amber-700'}`} dir="rtl">{testResult.breakdown.connector.arabic}</span>
                    </div>
                  </div>

                  {/* Clause 2 */}
                  <div className={`mb-3 p-3 rounded ${dk ? 'bg-gray-800' : 'bg-white'} border ${dk ? 'border-gray-600' : 'border-gray-200'}`}>
                    <p className={`text-xs font-medium mb-1 ${dk ? 'text-blue-400' : 'text-blue-600'}`}>Clause 2</p>
                    <div className="flex items-center gap-4">
                      <span className={`text-sm ${dk ? 'text-gray-300' : 'text-gray-600'}`}>{testResult.breakdown.clause2.english}</span>
                      <span className={`font-mono text-sm ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>{testResult.breakdown.clause2.latin}</span>
                      <span className={`font-mono text-sm ${dk ? 'text-amber-400' : 'text-amber-700'}`} dir="rtl">{testResult.breakdown.clause2.arabic}</span>
                    </div>
                  </div>

                  {/* Combined Result */}
                  <div className={`p-3 rounded border-2 ${dk ? 'bg-gray-800 border-emerald-600' : 'bg-emerald-50 border-emerald-400'}`}>
                    <p className={`text-xs font-bold mb-1 ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>Full Result</p>
                    <p className={`font-mono text-sm mb-1 ${dk ? 'text-emerald-300' : 'text-emerald-800'}`}>{testResult.result.latin}</p>
                    <p className={`font-mono text-sm ${dk ? 'text-amber-300' : 'text-amber-800'}`} dir="rtl">{testResult.result.arabic}</p>
                  </div>
                </div>
              ) : (
                <div>
                  <h4 className={`font-bold mb-2 text-sm ${dk ? 'text-amber-400' : 'text-amber-700'}`}>No Connector Detected</h4>
                  <p className={`text-xs mb-2 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Translated as single phrase:</p>
                  {testResult.full_translation && (
                    <div>
                      <p className={`font-mono text-sm ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>{testResult.full_translation.latin}</p>
                      <p className={`font-mono text-sm ${dk ? 'text-amber-400' : 'text-amber-700'}`} dir="rtl">{testResult.full_translation.arabic}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConnectorAdmin;
